import { IChannel } from "../../types/chat";
import { isChannelClosed } from "../../utils/chatClosed";
import {
  DEFAULT_SIDEBAR_FILTERS,
  GENERAL_CHAT_TYPE_KEY,
  ISidebarFilters,
  SIDEBAR_CHAT_TYPE_OPTIONS,
  SIDEBAR_FILTER_LAST_ACTIVE,
  SIDEBAR_FILTER_LAST_ACTIVE_LABELS,
  SIDEBAR_FILTER_SORT_BY,
  SIDEBAR_FILTER_SORT_BY_LABELS,
} from "../../types/sidebar";

const KNOWN_CHAT_TYPE_TAGS = new Set(
  SIDEBAR_CHAT_TYPE_OPTIONS.flatMap((option) => option.tags),
);

const LAST_ACTIVE_WINDOW_DAYS: Record<SIDEBAR_FILTER_LAST_ACTIVE, number | null> = {
  [SIDEBAR_FILTER_LAST_ACTIVE.any]: null,
  [SIDEBAR_FILTER_LAST_ACTIVE.last7Days]: 7,
  [SIDEBAR_FILTER_LAST_ACTIVE.last30Days]: 30,
};

/** True when none of the chat's tags map to a known chat-type option — the "General chat" bucket. */
function isGeneralChat(chat: IChannel): boolean {
  return !chat.tags?.some((tag) => KNOWN_CHAT_TYPE_TAGS.has(tag));
}

function matchesChatType(chat: IChannel, typeKey: string): boolean {
  if (typeKey === GENERAL_CHAT_TYPE_KEY) return isGeneralChat(chat);

  const option = SIDEBAR_CHAT_TYPE_OPTIONS.find((o) => o.key === typeKey);
  if (!option) return false;

  return !!chat.tags?.some((tag) => option.tags.includes(tag));
}

/** Whether a chat matches the open/closed state selected in the filter panel.
 * hideClosedChats=false (the default) keeps both open and closed chats;
 * hideClosedChats=true keeps only open chats — applied unconditionally in
 * Sidebar.tsx so ticking it takes effect even when no other filter is applied. */
export function matchesClosedFilter(chat: IChannel, hideClosedChats: boolean): boolean {
  return !hideClosedChats || !isChannelClosed(chat);
}

export function isDefaultSidebarFilters(filters: ISidebarFilters): boolean {
  return (
    filters.sortBy === DEFAULT_SIDEBAR_FILTERS.sortBy &&
    filters.lastActive === DEFAULT_SIDEBAR_FILTERS.lastActive &&
    filters.chatTypes.length === 0 &&
    filters.hideClosedChats === DEFAULT_SIDEBAR_FILTERS.hideClosedChats
  );
}

/** Number of non-default filter choices — sort, last-active and hide-closed-chats
 * count as one each when changed, plus one per selected chat type — shown as
 * the count on the SidebarFilterAction badge. */
export function getActiveSidebarFilterCount(filters: ISidebarFilters): number {
  let count = 0;

  if (filters.sortBy !== DEFAULT_SIDEBAR_FILTERS.sortBy) count += 1;
  if (filters.lastActive !== DEFAULT_SIDEBAR_FILTERS.lastActive) count += 1;
  if (filters.hideClosedChats !== DEFAULT_SIDEBAR_FILTERS.hideClosedChats) count += 1;
  count += filters.chatTypes.length;

  return count;
}

/** One line per non-default filter choice, e.g. "Sort: Name (A-Z)" or
 * "Chat type: Appeal, Policy question" — shown on the SidebarFilterAction
 * tooltip so the applied filters are visible without opening the panel. */
export function getActiveSidebarFilterSummaryLines(filters: ISidebarFilters): string[] {
  const lines: string[] = [];

  if (filters.sortBy !== DEFAULT_SIDEBAR_FILTERS.sortBy) {
    lines.push(`Sort: ${SIDEBAR_FILTER_SORT_BY_LABELS[filters.sortBy]}`);
  }

  if (filters.chatTypes.length > 0) {
    const labels = filters.chatTypes.map(
      (key) => SIDEBAR_CHAT_TYPE_OPTIONS.find((option) => option.key === key)?.label || key,
    );
    lines.push(`Chat type: ${labels.join(", ")}`);
  }

  if (filters.lastActive !== DEFAULT_SIDEBAR_FILTERS.lastActive) {
    lines.push(`Last active: ${SIDEBAR_FILTER_LAST_ACTIVE_LABELS[filters.lastActive]}`);
  }

  if (filters.hideClosedChats !== DEFAULT_SIDEBAR_FILTERS.hideClosedChats) {
    lines.push("Hide closed chats");
  }

  return lines;
}

/** Applies the sidebar "Filter chat history" panel's sort/type/recency choices
 * to a chat list, entirely client-side (no backend call, per REV request).
 * "Last activity" (the default) keeps the incoming order — the list as the
 * backend/user arranged it, including drag reordering — since IChannel carries
 * no activity timestamp to sort by; only "Date created" sorts by created_at. */
export function applySidebarFilters(
  chats: IChannel[],
  filters: ISidebarFilters,
): IChannel[] {
  let result = chats.filter((chat) => matchesClosedFilter(chat, filters.hideClosedChats));

  if (filters.chatTypes.length > 0) {
    result = result.filter((chat) =>
      filters.chatTypes.some((typeKey) => matchesChatType(chat, typeKey)),
    );
  }

  const windowDays = LAST_ACTIVE_WINDOW_DAYS[filters.lastActive];
  if (windowDays != null) {
    const threshold = Date.now() - windowDays * 24 * 60 * 60 * 1000;
    result = result.filter((chat) => {
      const createdAt = chat.created_at ? new Date(chat.created_at).getTime() : 0;
      return createdAt >= threshold;
    });
  }

  const sorted = [...result];
  switch (filters.sortBy) {
    case SIDEBAR_FILTER_SORT_BY.nameAsc:
      sorted.sort((a, b) =>
        (a.label || "").localeCompare(b.label || "", undefined, {
          sensitivity: "base",
        }),
      );
      break;
    case SIDEBAR_FILTER_SORT_BY.dateCreated:
      sorted.sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
      break;
    case SIDEBAR_FILTER_SORT_BY.lastActivity:
    default:
      // Keep the incoming order: re-sorting here would throw away the user's
      // drag order just for ticking a type/closed filter, while the sort
      // control still reads "Last activity (Default)".
      break;
  }

  return sorted;
}
